// COMPLETE UPDATED DiaryJournalView.jsx
// src/components/templates/diary/DiaryJournalView.jsx

import React, { useState, useMemo, useRef } from 'react';
import { 
  Book, Plus, Search, Calendar, Tag, Trash2, Edit3, X, Check,
  Smile, Frown, Meh, Heart, Zap, Coffee, Moon, Sun, Cloud,
  ChevronDown, ChevronUp, MoreVertical, Copy, Pin, PinOff,
  Clock, FileText, TrendingUp, Filter, Grid3X3, List,
  Briefcase, User, Activity, DollarSign, Users, Lightbulb,
  Image as ImageIcon, Paperclip, Save, Eye, EyeOff, Upload,
  File, Video, Music, Mic, Play, Loader, ExternalLink
} from 'lucide-react';
import { Button, Card, Badge } from './UIComponents';
import { VoiceRecorder } from '../shared/VoiceRecorder';
import { VoicePlayer } from '../shared/VoicePlayer';

// Domain types
const DiaryDomain = {
  PERSONAL: 'PERSONAL',
  WORK: 'WORK',
  HEALTH: 'HEALTH',
  FINANCE: 'FINANCE',
  SOCIAL: 'SOCIAL',
  IDEAS: 'IDEAS'
};

// Mood types
const MoodType = {
  GREAT: 'GREAT',
  GOOD: 'GOOD',
  OKAY: 'OKAY',
  LOW: 'LOW',
  BAD: 'BAD'
};

// Attachment types
const AttachmentType = {
  IMAGE: 'image',
  DOCUMENT: 'document',
  VIDEO: 'video',
  AUDIO: 'audio'
};

const getYoutubeId = (url) => {
  const match = url?.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
  return match ? match[1] : null;
};

const getYoutubeThumbnail = (url) => {
  const id = getYoutubeId(url);
  return id ? `https://img.youtube.com/vi/${id}/maxresdefault.jpg` : null;
};

// --- MOOD SELECTOR ---
const MoodSelector = ({ value, onChange }) => {
  const moods = [
    { key: MoodType.GREAT, icon: Zap, label: 'Great', color: 'text-green-500 bg-green-50 border-green-200' },
    { key: MoodType.GOOD, icon: Smile, label: 'Good', color: 'text-emerald-500 bg-emerald-50 border-emerald-200' },
    { key: MoodType.OKAY, icon: Meh, label: 'Okay', color: 'text-amber-500 bg-amber-50 border-amber-200' },
    { key: MoodType.LOW, icon: Cloud, label: 'Low', color: 'text-blue-500 bg-blue-50 border-blue-200' },
    { key: MoodType.BAD, icon: Frown, label: 'Bad', color: 'text-red-500 bg-red-50 border-red-200' },
  ];

  return (
    <div className="flex gap-2">
      {moods.map(mood => (
        <button
          key={mood.key}
          type="button"
          onClick={() => onChange(mood.key)}
          className={`p-2 rounded-lg border-2 transition-all ${
            value === mood.key 
              ? `${mood.color} border-current shadow-sm` 
              : 'border-gray-200 text-gray-400 hover:border-gray-300'
          }`}
          title={mood.label}
        >
          <mood.icon className="w-5 h-5" />
        </button>
      ))}
    </div>
  );
};

// --- DOMAIN BADGE ---
const DomainBadge = ({ domain, size = 'sm' }) => {
  const config = {
    [DiaryDomain.PERSONAL]: { icon: User, color: 'text-indigo-600 bg-indigo-50 border-indigo-200' },
    [DiaryDomain.WORK]: { icon: Briefcase, color: 'text-gray-600 bg-gray-100 border-gray-200' },
    [DiaryDomain.HEALTH]: { icon: Activity, color: 'text-green-600 bg-green-50 border-green-200' },
    [DiaryDomain.FINANCE]: { icon: DollarSign, color: 'text-emerald-600 bg-emerald-50 border-emerald-200' },
    [DiaryDomain.SOCIAL]: { icon: Users, color: 'text-pink-600 bg-pink-50 border-pink-200' },
    [DiaryDomain.IDEAS]: { icon: Lightbulb, color: 'text-amber-600 bg-amber-50 border-amber-200' },
  };

  const { icon: Icon, color } = config[domain] || config[DiaryDomain.PERSONAL];

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-md border text-xs font-bold uppercase tracking-wide ${color}`}>
      <Icon className={size === 'sm' ? 'w-3 h-3' : 'w-4 h-4'} />
      {domain}
    </span>
  );
};

// --- MOOD BADGE ---
const MoodBadge = ({ mood }) => {
  if (!mood) return null;

  const config = {
    [MoodType.GREAT]: { icon: Zap, color: 'text-green-600 bg-green-50' },
    [MoodType.GOOD]: { icon: Smile, color: 'text-emerald-600 bg-emerald-50' },
    [MoodType.OKAY]: { icon: Meh, color: 'text-amber-600 bg-amber-50' },
    [MoodType.LOW]: { icon: Cloud, color: 'text-blue-600 bg-blue-50' },
    [MoodType.BAD]: { icon: Frown, color: 'text-red-600 bg-red-50' },
  };

  const { icon: Icon, color } = config[mood] || config[MoodType.OKAY];

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${color}`}>
      <Icon className="w-3 h-3" />
      {mood}
    </span>
  );
};

// --- ATTACHMENT PREVIEW ---
const AttachmentPreview = ({ attachment, onRemove, onPlay }) => {
  const getIcon = () => {
    switch (attachment.type) {
      case AttachmentType.IMAGE: return ImageIcon;
      case AttachmentType.VIDEO: return Video;
      case AttachmentType.AUDIO: return Music;
      case AttachmentType.DOCUMENT: return File;
      default: return File;
    }
  };

  const Icon = getIcon();
  const isYoutube = attachment.type === AttachmentType.VIDEO && attachment.youtubeUrl;

  return (
    <div className="relative group bg-white border border-gray-200 rounded-lg p-3 hover:shadow-md transition-all">
      {attachment.type === AttachmentType.IMAGE && attachment.url && (
        <img 
          src={attachment.url} 
          alt={attachment.name} 
          className="w-full h-24 object-cover rounded mb-2"
        />
      )}

      {isYoutube && attachment.thumbnailUrl && (
        <div className="relative w-full h-24 rounded mb-2 overflow-hidden bg-black">
          <img 
            src={attachment.thumbnailUrl} 
            alt={attachment.name} 
            className="w-full h-full object-cover opacity-80"
          />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center">
              <Play className="w-6 h-6 text-white ml-1" />
            </div>
          </div>
        </div>
      )}
      
      <div className="flex items-start gap-2">
        <div className="flex-shrink-0">
          <Icon className="w-4 h-4 text-gray-400" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium text-gray-700 truncate">{attachment.name}</p>
          {attachment.size && (
            <p className="text-xs text-gray-400">{(attachment.size / 1024).toFixed(1)} KB</p>
          )}
          {isYoutube && (
            <p className="text-xs text-red-600 flex items-center gap-1 mt-1">
              <Video className="w-3 h-3" /> YouTube
            </p>
          )}
        </div>
        
        <div className="flex items-center gap-1">
          {attachment.type === AttachmentType.AUDIO && onPlay && (
            <button
              onClick={() => onPlay(attachment)}
              className="p-1 hover:bg-gray-100 rounded transition-colors"
              title="Play audio"
            >
              <Play className="w-3 h-3 text-indigo-600" />
            </button>
          )}
          {attachment.url && (
            <a
              href={attachment.url}
              target="_blank"
              rel="noopener noreferrer"
              className="p-1 hover:bg-gray-100 rounded transition-colors"
              title="Open"
            >
              <ExternalLink className="w-3 h-3 text-gray-600" />
            </a>
          )}
          {onRemove && (
            <button
              onClick={() => onRemove(attachment.id)}
              className="p-1 hover:bg-red-100 rounded transition-colors"
              title="Remove"
            >
              <X className="w-3 h-3 text-red-600" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ADD this new component after AttachmentPreview
const YoutubeAttachmentPreview = ({ attachment, onRemove }) => {
  const youtubeId = getYoutubeId(attachment.youtubeUrl);

  return (
    <div className="relative group bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-all">
      {youtubeId && (
        <div className="aspect-video bg-black">
          <iframe
            src={`https://www.youtube.com/embed/${youtubeId}`}
            title={attachment.name}
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="w-full h-full"
          />
        </div>
      )}
      
      <div className="p-3">
        <div className="flex items-start gap-2">
          <div className="flex-shrink-0">
            <Video className="w-4 h-4 text-red-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium text-gray-700 truncate">{attachment.name}</p>
            <p className="text-xs text-red-600 flex items-center gap-1 mt-1">
              YouTube Video
            </p>
          </div>
          
          {onRemove && (
            <button
              onClick={() => onRemove(attachment.id)}
              className="p-1 hover:bg-red-100 rounded transition-colors flex-shrink-0"
              title="Remove"
            >
              <X className="w-3 h-3 text-red-600" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// --- STATS BAR ---
const StatsBar = ({ entries }) => {
  const totalEntries = entries.length;
  const totalWords = entries.reduce((sum, e) => sum + (e.content?.split(/\s+/).filter(Boolean).length || 0), 0);
  const domainCounts = entries.reduce((acc, e) => {
    acc[e.domain] = (acc[e.domain] || 0) + 1;
    return acc;
  }, {});

  const topDomain = Object.keys(domainCounts).reduce((a, b) => domainCounts[a] > domainCounts[b] ? a : b, 'PERSONAL');

  return (
    <div className="flex flex-wrap gap-4 text-sm">
      <div className="flex items-center gap-2">
        <FileText className="w-4 h-4 text-gray-400" />
        <span className="font-medium text-gray-900">{totalEntries}</span>
        <span className="text-gray-500">entries</span>
      </div>
      <div className="flex items-center gap-2">
        <TrendingUp className="w-4 h-4 text-gray-400" />
        <span className="font-medium text-gray-900">{totalWords.toLocaleString()}</span>
        <span className="text-gray-500">words</span>
      </div>
      <div className="flex items-center gap-2">
        <Filter className="w-4 h-4 text-gray-400" />
        <span className="text-gray-500">Most: <span className="font-medium text-gray-900">{topDomain}</span></span>
      </div>
    </div>
  );
};

// --- ENTRY CARD ---
const EntryCard = ({ entry, viewMode, onEdit, onDelete, onPin, onPlayAudio }) => {
  const [expanded, setExpanded] = useState(false);
  const wordCount = entry.content?.split(/\s+/).filter(Boolean).length || 0;
  const shouldTruncate = wordCount > 100;

  const displayContent = expanded || !shouldTruncate 
    ? entry.content 
    : entry.content?.split(/\s+/).slice(0, 100).join(' ') + '...';

  // Grid view (card)
  if (viewMode === 'grid') {
    return (
      <div className={`bg-white rounded-xl shadow-sm border border-gray-200 p-5 hover:shadow-md transition-shadow break-inside-avoid-column relative ${entry.isPinned ? 'ring-2 ring-amber-400' : ''}`}>
        {entry.isPinned && (
          <Pin className="absolute -top-2 -right-2 w-5 h-5 text-amber-500 fill-amber-500" />
        )}

        <div className="flex justify-between items-start mb-3">
          <div className="flex items-center gap-2">
            <DomainBadge domain={entry.domain} />
            <MoodBadge mood={entry.mood} />
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => onEdit?.(entry)} className="text-gray-300 hover:text-indigo-500 transition-colors">
              <Edit3 className="w-4 h-4" />
            </button>
            <button onClick={() => onPin?.({ ...entry, isPinned: !entry.isPinned })} className={`transition-colors ${entry.isPinned ? 'text-amber-500' : 'text-gray-300 hover:text-amber-500'}`}>
              {entry.isPinned ? <Pin className="w-4 h-4 fill-amber-500" /> : <PinOff className="w-4 h-4" />}
            </button>
            <button onClick={() => onDelete?.(entry.id)} className="text-gray-300 hover:text-red-500 transition-colors">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>

        <p className="text-xs text-gray-400 mb-2">
          {new Date(entry.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
          {entry.timestamp && <span className="ml-2">{entry.timestamp}</span>}
        </p>

        <h3 className="font-bold text-gray-900 text-lg mb-2">{entry.title}</h3>
        <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-wrap">{displayContent}</p>

        {shouldTruncate && (
          <button onClick={() => setExpanded(!expanded)} className="mt-2 text-xs text-indigo-600 hover:text-indigo-700 font-medium flex items-center">
            {expanded ? <><ChevronUp className="w-3 h-3 mr-1" /> Show less</> : <><ChevronDown className="w-3 h-3 mr-1" /> Read more</>}
          </button>
        )}

        {/* Attachments Preview */}
        {entry.attachments && entry.attachments.length > 0 && (
          <div className="mt-4 pt-3 border-t border-gray-100">
            <div className="grid grid-cols-2 gap-2">
              {entry.attachments.slice(0, 2).map(att => (
                <div key={att.id} className="flex items-center gap-2 text-xs text-gray-500 bg-gray-50 px-2 py-1.5 rounded">
                  {att.type === AttachmentType.IMAGE && <ImageIcon className="w-3 h-3" />}
                  {att.type === AttachmentType.VIDEO && <Video className="w-3 h-3" />}
                  {att.type === AttachmentType.AUDIO && <Music className="w-3 h-3" />}
                  {att.type === AttachmentType.DOCUMENT && <File className="w-3 h-3" />}
                  <span className="truncate">{att.name}</span>
                </div>
              ))}
            </div>
            {entry.attachments.length > 2 && (
              <p className="text-xs text-gray-400 mt-2">+{entry.attachments.length - 2} more</p>
            )}
          </div>
        )}

        {/* Tags */}
        {entry.tags && entry.tags.length > 0 && (
          <div className="mt-4 pt-3 border-t border-gray-100 flex flex-wrap gap-2">
            {entry.tags.map(tag => (
              <span key={tag} className="inline-flex items-center text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                <Tag className="w-3 h-3 mr-1 opacity-50" /> {tag}
              </span>
            ))}
          </div>
        )}

        {/* Footer */}
        <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between text-xs text-gray-400">
          <span>{wordCount} words</span>
          {entry.attachments?.length > 0 && (
            <span className="flex items-center"><Paperclip className="w-3 h-3 mr-1" /> {entry.attachments.length}</span>
          )}
        </div>
      </div>
    );
  }

  // Timeline view (default)
  return (
    <div className="relative pl-8 md:pl-0 group">
      {/* Timeline Line */}
      <div className="hidden md:block absolute left-[150px] top-0 bottom-0 w-px bg-indigo-100 group-last:hidden" />
      <div className="hidden md:block absolute left-[146px] top-6 w-2.5 h-2.5 rounded-full bg-indigo-400 border-2 border-white shadow-sm z-10" />
      
      {/* Mobile timeline dot */}
      <div className="md:hidden absolute left-0 top-6 w-2.5 h-2.5 rounded-full bg-indigo-400 border-2 border-white shadow-sm" />
      
      <div className="md:flex gap-8">
        {/* Date Column */}
        <div className="md:w-[150px] shrink-0 text-left md:text-right pt-4 mb-2 md:mb-0">
          <p className="font-bold text-gray-800">
            {new Date(entry.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
          </p>
          <p className="text-xs text-gray-400 font-mono">{entry.timestamp}</p>
          <MoodBadge mood={entry.mood} />
        </div>
        
        {/* Card Content */}
        <div className="flex-1">
          <div className={`bg-white rounded-xl shadow-sm border border-gray-200 p-5 hover:shadow-md transition-shadow relative ${entry.isPinned ? 'ring-2 ring-amber-400' : ''}`}>
            {entry.isPinned && (
              <Pin className="absolute -top-2 -right-2 w-5 h-5 text-amber-500 fill-amber-500" />
            )}

            <div className="flex justify-between items-start mb-3">
              <DomainBadge domain={entry.domain} />
              <div className="flex items-center gap-2">
                <button onClick={() => onEdit?.(entry)} className="text-gray-300 hover:text-indigo-500 transition-colors">
                  <Edit3 className="w-4 h-4" />
                </button>
                <button onClick={() => onPin?.({ ...entry, isPinned: !entry.isPinned })} className={`transition-colors ${entry.isPinned ? 'text-amber-500' : 'text-gray-300 hover:text-amber-500'}`}>
                  {entry.isPinned ? <Pin className="w-4 h-4 fill-amber-500" /> : <PinOff className="w-4 h-4" />}
                </button>
                <button onClick={() => onDelete?.(entry.id)} className="text-gray-300 hover:text-red-500 transition-colors">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>

            <h3 className="font-bold text-gray-900 text-lg mb-2">{entry.title}</h3>
            <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-wrap">{displayContent}</p>

            {shouldTruncate && (
              <button onClick={() => setExpanded(!expanded)} className="mt-2 text-xs text-indigo-600 hover:text-indigo-700 font-medium flex items-center">
                {expanded ? <><ChevronUp className="w-3 h-3 mr-1" /> Show less</> : <><ChevronDown className="w-3 h-3 mr-1" /> Read more</>}
              </button>
            )}

            {/* Attachments */}
            {entry.attachments && entry.attachments.length > 0 && (
            <div className="mt-4 pt-3 border-t border-gray-100">
              <div className="grid grid-cols-1 gap-3">
                {entry.attachments.map(att => {
                  // Show YouTube videos with embedded player
                  if (att.type === AttachmentType.VIDEO && att.youtubeUrl) {
                    const youtubeId = getYoutubeId(att.youtubeUrl);
                    return youtubeId ? (
                      <div key={att.id} className="aspect-video bg-black rounded-lg overflow-hidden">
                        <iframe
                          src={`https://www.youtube.com/embed/${youtubeId}`}
                          title={att.name}
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                          className="w-full h-full"
                        />
                      </div>
                    ) : null;
                  }
                  
                  // Show other attachments with standard preview
                  return (
                    <AttachmentPreview 
                      key={att.id} 
                      attachment={att} 
                      onPlay={att.type === AttachmentType.AUDIO ? onPlayAudio : null}
                    />
                  );
                })}
              </div>
            </div>
          )}

            {/* Tags */}
            {entry.tags && entry.tags.length > 0 && (
              <div className="mt-4 pt-3 border-t border-gray-100 flex flex-wrap gap-2">
                {entry.tags.map(tag => (
                  <span key={tag} className="inline-flex items-center text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                    <Tag className="w-3 h-3 mr-1 opacity-50" /> {tag}
                  </span>
                ))}
              </div>
            )}

            {/* Footer */}
            <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between text-xs text-gray-400">
              <span>{wordCount} words</span>
              {entry.attachments?.length > 0 && (
                <span className="flex items-center"><Paperclip className="w-3 h-3 mr-1" /> {entry.attachments.length}</span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// --- MAIN COMPONENT ---
const DiaryJournalView = ({ 
  bookId,
  entries = [], 
  customStyling = {}, 
  onAddEntry, 
  onUpdateEntry, 
  onDeleteEntry 
}) => {
  const [viewMode, setViewMode] = useState('TIMELINE'); // 'TIMELINE', 'GRID', 'EDITOR'
  const [displayMode, setDisplayMode] = useState('timeline'); // 'timeline' or 'grid'
  const [filterDomain, setFilterDomain] = useState('ALL');
  const [searchQuery, setSearchQuery] = useState('');
  const [showPinnedOnly, setShowPinnedOnly] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [youtubeUrl, setYoutubeUrl] = useState('');

  // Editor State
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    domain: DiaryDomain.PERSONAL,
    mood: null,
    date: new Date().toISOString().split('T')[0],
    tags: [],
    isPrivate: false,
    attachments: []
  });
  const [tagInput, setTagInput] = useState('');
  
  // Media upload state
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [showRecorder, setShowRecorder] = useState(false);
  const [activePlayer, setActivePlayer] = useState(null);
  const fileInputRef = useRef(null);

  // Filter and sort entries
  const filteredEntries = useMemo(() => {
    return entries
      .filter(e => {
        const matchesDomain = filterDomain === 'ALL' || e.domain === filterDomain;
        const matchesSearch = 
          e.title?.toLowerCase().includes(searchQuery.toLowerCase()) || 
          e.content?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          (e.tags || []).some(t => t.toLowerCase().includes(searchQuery.toLowerCase()));
        const matchesPinned = !showPinnedOnly || e.isPinned;
        return matchesDomain && matchesSearch && matchesPinned;
      })
      .sort((a, b) => {
        // Pinned first
        if (a.isPinned && !b.isPinned) return -1;
        if (!a.isPinned && b.isPinned) return 1;
        // Then by date
        const da = new Date(a.date + 'T' + (a.timestamp || '00:00'));
        const db = new Date(b.date + 'T' + (b.timestamp || '00:00'));
        return db.getTime() - da.getTime();
      });
  }, [entries, filterDomain, searchQuery, showPinnedOnly]);

  const handleFileSelect = async (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0 || !bookId) return;

    setUploading(true);
    setUploadError('');

    try {
      const { firestoreService } = await import('../../services/firestoreService');
      
      const uploadedAttachments = await Promise.all(
        files.map(async (file) => {
          // Validate file size (10MB limit)
          if (file.size > 10 * 1024 * 1024) {
            throw new Error(`File ${file.name} exceeds 10MB limit`);
          }

          // Determine attachment type
          let attachmentType = AttachmentType.DOCUMENT;
          let storagePath = 'documents';
          
          if (file.type.startsWith('image/')) {
            attachmentType = AttachmentType.IMAGE;
            storagePath = 'images';
          } else if (file.type.startsWith('video/')) {
            attachmentType = AttachmentType.VIDEO;
            storagePath = 'videos';
          } else if (file.type.startsWith('audio/')) {
            attachmentType = AttachmentType.AUDIO;
            storagePath = 'audio';
          }

          // Upload file
          const uploadPath = `pb-${bookId}/diary/journal/${storagePath}`;
          const result = await firestoreService.uploadFile(file, uploadPath);

          return {
            id: Date.now() + Math.random(),
            name: file.name,
            type: attachmentType,
            size: file.size,
            url: result.url,
            uploadedAt: new Date().toISOString()
          };
        })
      );

      setFormData(prev => ({
        ...prev,
        attachments: [...prev.attachments, ...uploadedAttachments]
      }));

      console.log('Files uploaded successfully:', uploadedAttachments);
    } catch (error) {
      console.error('Upload error:', error);
      setUploadError(error.message || 'Failed to upload files');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleSaveVoiceNote = async ({ blob, duration, speaker }) => {
    if (!bookId) return;

    setUploading(true);
    setUploadError('');

    try {
      const { firestoreService } = await import('../../services/firestoreService');
      
      const fileName = `voice-${Date.now()}.webm`;
      const uploadPath = `pb-${bookId}/diary/journal/audio`;
      const result = await firestoreService.uploadFile(blob, uploadPath);

      const audioAttachment = {
        id: Date.now() + Math.random(),
        name: fileName,
        type: AttachmentType.AUDIO,
        size: blob.size,
        url: result.url,
        duration: duration,
        speaker: speaker,
        uploadedAt: new Date().toISOString()
      };

      setFormData(prev => ({
        ...prev,
        attachments: [...prev.attachments, audioAttachment]
      }));

      setShowRecorder(false);
      console.log('Voice note saved:', audioAttachment);
    } catch (error) {
      console.error('Voice save error:', error);
      setUploadError(error.message || 'Failed to save voice note');
    } finally {
      setUploading(false);
    }
  };

  const handleAddYoutubeVideo = () => {
    if (!youtubeUrl.trim()) return;

    const youtubeId = getYoutubeId(youtubeUrl);
    if (!youtubeId) {
      setUploadError('Invalid YouTube URL');
      return;
    }

    const videoAttachment = {
      id: Date.now() + Math.random(),
      name: `YouTube Video`,
      type: AttachmentType.VIDEO,
      youtubeUrl: youtubeUrl,
      url: `https://www.youtube.com/watch?v=${youtubeId}`,
      thumbnailUrl: getYoutubeThumbnail(youtubeUrl),
      uploadedAt: new Date().toISOString()
    };

    setFormData(prev => ({
      ...prev,
      attachments: [...prev.attachments, videoAttachment]
    }));

    setYoutubeUrl('');
    setUploadError('');
  };

  const handleRemoveAttachment = (attachmentId) => {
    setFormData(prev => ({
      ...prev,
      attachments: prev.attachments.filter(a => a.id !== attachmentId)
    }));
  };

  const handlePlayAudio = (attachment) => {
    setActivePlayer({
      url: attachment.url,
      duration: attachment.duration,
      speaker: attachment.speaker
    });
  };
  
  const handleSave = () => {
    if (!formData.title || !formData.content) return;

    const entry = {
      id: editingEntry?.id || Math.random().toString(36).substr(2, 9),
      date: formData.date || new Date().toISOString().split('T')[0],
      timestamp: editingEntry?.timestamp || new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      domain: formData.domain,
      mood: formData.mood,
      title: formData.title,
      content: formData.content,
      tags: formData.tags,
      isPrivate: formData.isPrivate,
      attachments: formData.attachments,
      isPinned: editingEntry?.isPinned || false,
      updatedAt: new Date().toISOString()
    };

    if (editingEntry) {
      onUpdateEntry?.(entry);
    } else {
      onAddEntry?.(entry);
    }
    resetForm();
  };

  const resetForm = () => {
    setFormData({
      title: '',
      content: '',
      domain: DiaryDomain.PERSONAL,
      mood: null,
      date: new Date().toISOString().split('T')[0],
      tags: [],
      isPrivate: false,
      attachments: []
    });
    setTagInput('');
    setYoutubeUrl('');
    setEditingEntry(null);
    setViewMode('TIMELINE');
    setUploadError('');
  };

  const handleEdit = (entry) => {
    setEditingEntry(entry);
    setFormData({
      title: entry.title,
      content: entry.content,
      domain: entry.domain,
      mood: entry.mood,
      date: entry.date,
      tags: entry.tags || [],
      isPrivate: entry.isPrivate || false,
      attachments: entry.attachments || []
    });
    setViewMode('EDITOR');
  };

  const addTag = () => {
    if (!tagInput.trim()) return;
    if (formData.tags.includes(tagInput.trim())) return;
    setFormData(prev => ({ ...prev, tags: [...prev.tags, tagInput.trim()] }));
    setTagInput('');
  };

  const removeTag = (tag) => {
    setFormData(prev => ({ ...prev, tags: prev.tags.filter(t => t !== tag) }));
  };

  const handlePin = (entry) => {
    onUpdateEntry?.({ ...entry, isPinned: !entry.isPinned });
  };

  const domainOptions = [
    { key: 'ALL', label: 'All Domains', icon: Book },
    { key: DiaryDomain.PERSONAL, label: 'Personal', icon: User },
    { key: DiaryDomain.WORK, label: 'Work', icon: Briefcase },
    { key: DiaryDomain.HEALTH, label: 'Health', icon: Activity },
    { key: DiaryDomain.FINANCE, label: 'Finance', icon: DollarSign },
    { key: DiaryDomain.SOCIAL, label: 'Social', icon: Users },
    { key: DiaryDomain.IDEAS, label: 'Ideas', icon: Lightbulb },
  ];

  const wordCount = formData.content?.split(/\s+/).filter(Boolean).length || 0;

  return (
    <div className="flex-1 flex-col overflow-y-auto h-full bg-gray-50">
      
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 shrink-0">
        <div className="flex flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center">
              <Book className="w-6 h-6 mr-2"
                style={{ color: customStyling.primaryColor }}
              />
              {viewMode === 'EDITOR' ? (editingEntry ? 'Edit Entry' : 'New Entry') : 'Journal & Diary'}
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              {viewMode === 'EDITOR' 
                ? 'Capture your thoughts and reflections'
                : `${entries.length} entries across your life domains`
              }
            </p>
          </div>
          
          {viewMode !== 'EDITOR' ? (
            <Button onClick={() => setViewMode('EDITOR')} className="shadow-lg shadow-indigo-200"
            style={{ backgroundColor: customStyling.primaryColor }}
            >
              <Plus className="w-4 h-4" /> 
              <span className=" hidden sm:inline"> New Entry </span>
            </Button>
          ) : (
            <Button variant="secondary" onClick={resetForm}>
              <X className="w-4 h-4 mr-2" /> Cancel
            </Button>
          )}
        </div>

        {/* Stats Bar - only in list view */}
        {viewMode !== 'EDITOR' && (
          <div className="mt-4">
            <StatsBar entries={entries} />
          </div>
        )}
      </div>

      {viewMode !== 'EDITOR' ? (
        <div className="flex-1 overflow-hidden flex flex-col">
          {/* Filters */}
          <div className="p-4 border-b border-gray-200 bg-white shrink-0">
            <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center">
              {/* Search */}
              <div className="relative flex-1 w-full">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input 
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 transition-all"
                  placeholder="Search entries, tags..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>

              {/* Domain Filter */}
              <div className="flex flex-wrap gap-2">
                {domainOptions.slice(0, 4).map(opt => (
                  <button
                    key={opt.key}
                    onClick={() => setFilterDomain(opt.key)}
                    className={`
                      px-3 py-1.5 rounded-full text-xs font-medium border transition-all flex items-center gap-1.5
                      ${
                        filterDomain === opt.key 
                          ? 'text-white' 
                          : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                      }
                    `}
                    style={
                      filterDomain === opt.key
                        ? {
                            backgroundColor: customStyling.primaryColor,
                            borderColor: customStyling.primaryColor
                          }
                        : {}
                    }
                  >
                    <opt.icon className="w-3 h-3" />
                    {opt.label}
                  </button>

                ))}
                <select
                  value={filterDomain}
                  onChange={(e) => setFilterDomain(e.target.value)}
                  className="px-3 py-1.5 rounded-lg text-xs font-medium border border-gray-200 bg-white text-gray-600 outline-none"
                >
                  {domainOptions.map(opt => (
                    <option key={opt.key} value={opt.key}>{opt.label}</option>
                  ))}
                </select>
              </div>

              {/* View Toggle & Pinned Filter */}
              <div className="flex items-center gap-2">
                {entries.some(e => e.isPinned) && (
                  <button
                    onClick={() => setShowPinnedOnly(!showPinnedOnly)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all flex items-center gap-1.5 ${
                      showPinnedOnly 
                        ? 'bg-amber-500 text-white border-amber-500' 
                        : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <Pin className={`w-3 h-3 ${showPinnedOnly ? 'fill-white' : ''}`} />
                    Pinned
                  </button>
                )}

                <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setDisplayMode('timeline')}
                    className={`p-2 ${displayMode === 'timeline' ? 'bg-indigo-50 text-indigo-600' : 'bg-white text-gray-500 hover:bg-gray-50'}`}
                    title="Timeline view"
                  >
                    <List className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setDisplayMode('grid')}
                    className={`p-2 ${displayMode === 'grid' ? 'bg-indigo-50 text-indigo-600' : 'bg-white text-gray-500 hover:bg-gray-50'}`}
                    title="Grid view"
                  >
                    <Grid3X3 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Entries List */}
          <div className="flex-1 overflow-y-auto p-6 bg-gray-50">
            <div className={displayMode === 'grid' ? "max-w-7xl mx-auto" : "max-w-3xl mx-auto"}>
              {filteredEntries.length === 0 ? (
                <div className="text-center py-16">
                  <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    {searchQuery || filterDomain !== 'ALL' || showPinnedOnly ? (
                      <Search className="w-10 h-10 text-gray-300" />
                    ) : (
                      <Book className="w-10 h-10 text-gray-300" />
                    )}
                  </div>
                  <h3 className="text-lg font-semibold text-gray-700 mb-1">
                    {searchQuery || filterDomain !== 'ALL' || showPinnedOnly ? 'No matching entries' : 'No journal entries yet'}
                  </h3>
                  <p className="text-gray-500 text-sm mb-6">
                    {searchQuery || filterDomain !== 'ALL' || showPinnedOnly
                      ? 'Try adjusting your filters'
                      : 'Start capturing your thoughts and experiences'
                    }
                  </p>
                  {!searchQuery && filterDomain === 'ALL' && !showPinnedOnly && (
                    <Button onClick={() => setViewMode('EDITOR')}>
                      <Plus className="w-4 h-4 mr-2" /> Write First Entry
                    </Button>
                  )}
                  {(searchQuery || filterDomain !== 'ALL' || showPinnedOnly) && (
                    <button
                      onClick={() => { setSearchQuery(''); setFilterDomain('ALL'); setShowPinnedOnly(false); }}
                      className="text-indigo-600 hover:text-indigo-700 text-sm font-medium"
                    >
                      Clear filters
                    </button>
                  )}
                </div>
              ) : (
                <div className={displayMode === 'grid' ? "columns-1 md:columns-2 lg:columns-3 gap-6 space-y-6" : "space-y-8"}>
                  {filteredEntries.map(entry => (
                    <EntryCard 
                      key={entry.id}
                      entry={entry}
                      viewMode={displayMode}
                      onEdit={handleEdit}
                      onDelete={onDeleteEntry}
                      onPin={handlePin}
                      onPlayAudio={handlePlayAudio}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        /* Editor View */
        <div className="flex-1 overflow-y-auto p-6">
          <div className="max-w-3xl mx-auto">
            <Card className="p-8">
              <div className="space-y-6">
                {/* Date, Domain, Mood Row */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Date</label>
                    <input 
                      type="date"
                      value={formData.date}
                      onChange={e => setFormData({...formData, date: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Domain</label>
                    <select 
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      value={formData.domain}
                      onChange={e => setFormData({...formData, domain: e.target.value})}
                    >
                      <option value="PERSONAL">Personal</option>
                      <option value="WORK">Work</option>
                      <option value="HEALTH">Health</option>
                      <option value="FINANCE">Finance</option>
                      <option value="SOCIAL">Social</option>
                      <option value="IDEAS">Ideas</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Mood</label>
                    <MoodSelector value={formData.mood} onChange={mood => setFormData({...formData, mood})} />
                  </div>
                </div>

                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Title / Headline</label>
                  <input 
                    type="text"
                    placeholder="What's this entry about?"
                    value={formData.title}
                    onChange={e => setFormData({...formData, title: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg text-lg font-medium focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                {/* Content */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label className="block text-sm font-medium text-gray-700">Entry Content</label>
                    <span className="text-xs text-gray-400">{wordCount} words</span>
                  </div>
                  <textarea 
                    rows={4}
                    placeholder="What happened? Thoughts, reflections, details..."
                    value={formData.content}
                    onChange={e => setFormData({...formData, content: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-md text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
                  />
                </div>

                {/* Media Attachments */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Attachments
                  </label>
                  
                  {/* Upload Buttons */}
                  <div className="flex flex-col gap-3 mb-4">
                    <div className="flex gap-2">
                      <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.txt"
                        onChange={handleFileSelect}
                        className="hidden"
                      />
                      <Button 
                        variant="secondary" 
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading}
                        className="flex-1"
                      >
                        {uploading ? (
                          <>
                            <Loader className="w-4 h-4 mr-2 animate-spin" />
                            Uploading...
                          </>
                        ) : (
                          <>
                            <Upload className="w-4 h-4 mr-2" />
                            Upload Files
                          </>
                        )}
                      </Button>
                      <Button 
                        variant="secondary"
                        onClick={() => setShowRecorder(true)}
                        disabled={uploading}
                      >
                        <Mic className="w-4 h-4 mr-2" />
                        Voice Note
                      </Button>
                    </div>

                    {/* YouTube URL Input */}
                    <div className="flex gap-2">
                      <div className="flex-1 relative">
                        <Video className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                        <input
                          type="url"
                          value={youtubeUrl}
                          onChange={(e) => setYoutubeUrl(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddYoutubeVideo())}
                          placeholder="Paste YouTube URL..."
                          className="w-full pl-10 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                      </div>
                      <Button 
                        variant="secondary"
                        onClick={handleAddYoutubeVideo}
                        disabled={!youtubeUrl.trim()}
                      >
                        Add Video
                      </Button>
                    </div>
                  </div>

                  {uploadError && (
                    <p className="text-xs text-red-600 mb-3">{uploadError}</p>
                  )}

                  {/* Attachments List */}
                  {formData.attachments.length > 0 && (
                    <div className="grid grid-cols-1 gap-3">
                      {formData.attachments.map(attachment => {
                        // Show YouTube videos with embedded player
                        if (attachment.type === AttachmentType.VIDEO && attachment.youtubeUrl) {
                          return (
                            <YoutubeAttachmentPreview
                              key={attachment.id}
                              attachment={attachment}
                              onRemove={handleRemoveAttachment}
                            />
                          );
                        }
                        
                        // Show other attachments with standard preview
                        return (
                          <AttachmentPreview
                            key={attachment.id}
                            attachment={attachment}
                            onRemove={handleRemoveAttachment}
                            onPlay={attachment.type === AttachmentType.AUDIO ? handlePlayAudio : null}
                          />
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Tags */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Tags</label>
                  <div className="flex gap-2 mb-3">
                    <input 
                      type="text"
                      placeholder="Add a tag..." 
                      value={tagInput}
                      onChange={e => setTagInput(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addTag())}
                      className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                    <Button variant="secondary" onClick={addTag}>
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                  {formData.tags.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {formData.tags.map(tag => (
                        <span key={tag} className="bg-indigo-50 text-indigo-700 px-3 py-1 rounded-full text-sm border border-indigo-100 flex items-center">
                          #{tag}
                          <button onClick={() => removeTag(tag)} className="ml-2 hover:text-indigo-900">
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Privacy Toggle */}
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, isPrivate: !prev.isPrivate }))}
                    className={`p-2 rounded-lg border-2 transition-all ${
                      formData.isPrivate 
                        ? 'border-indigo-500 bg-indigo-50 text-indigo-600' 
                        : 'border-gray-200 text-gray-400 hover:border-gray-300'
                    }`}
                  >
                    {formData.isPrivate ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                  <span className="text-sm text-gray-600">
                    {formData.isPrivate ? 'Private entry (hidden from shared views)' : 'Visible entry'}
                  </span>
                </div>

                {/* Actions */}
                <div className="pt-6 flex justify-end gap-3 border-t border-gray-100">
                  <Button variant="secondary" onClick={resetForm}>
                    <X className="w-4 h-4 mr-2" /> Discard
                  </Button>
                  <Button onClick={handleSave} disabled={!formData.title || !formData.content}>
                    <Save className="w-4 h-4 mr-2" /> {editingEntry ? 'Update Entry' : 'Save Entry'}
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        </div>
      )}

      {/* Voice Recorder Modal */}
      {showRecorder && (
        <VoiceRecorder
          context="journal"
          maxDuration={300}
          onSave={handleSaveVoiceNote}
          onCancel={() => setShowRecorder(false)}
        />
      )}

      {/* Voice Player */}
      {activePlayer && (
        <VoicePlayer
          voice={activePlayer}
          title="Journal Voice Note"
          autoPlay={false}
          onClose={() => setActivePlayer(null)}
        />
      )}
    </div>
  );
};

export default DiaryJournalView;